import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Upload, Filter, CircleDollarSign, Loader2, MailSearch } from 'lucide-react';
import { toast } from 'sonner';
import { conciliacionProveedoresApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useRegisterCmdKCommands } from '../../contexts/CmdKContext';
import { normRut } from './utils';
import { BandejaList } from './components/BandejaList';
import { DetalleConciliacion } from './components/DetalleConciliacion';
import { UploadDialog } from './components/UploadDialog';
import { BloqueadosSection } from './components/BloqueadosSection';
import { ConfiguracionSection } from './components/ConfiguracionSection';
import { InformePendientesSection } from './components/InformePendientesSection';

export default function ConciliacionProveedoresPage() {
  const { user } = useAuth();
  const isGerencia = ['admin', 'gerencia'].includes((user?.rol || '').toLowerCase());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter 308.d — Pendientes es el filtro por default (lo que necesita atención)
  const [filter, setFilter] = useState('pendiente_revision');
  const [tab, setTab] = useState('bandeja');
  const [selected, setSelected] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [bloqueadosCount, setBloqueadosCount] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const selectedIdRef = useRef(null);

  // Deep-link ?rut=XXX: auto-filtra la bandeja por ese proveedor
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const rutFilter = urlParams?.get('rut') || '';

  useEffect(() => { selectedIdRef.current = selected?.id || null; }, [selected]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await conciliacionProveedoresApi.list({ limit: 500, include_superadas: true });
      const data = res.data?.items || [];
      setItems(data);
      const sid = selectedIdRef.current;
      if (sid && data.some((x) => x.id === sid)) {
        try {
          const det = await conciliacionProveedoresApi.get(sid);
          setSelected(det.data);
        } catch { /* keep old */ }
      }
    } catch (e) {
      toast.error('Error cargando bandeja');
    } finally { setLoading(false); }
  }, []);

  const loadBloqueadosCount = useCallback(async () => {
    try {
      const res = await conciliacionProveedoresApi.listBloqueados();
      setBloqueadosCount(res.data?.count ?? (res.data?.items?.length ?? 0));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); loadBloqueadosCount(); }, [load, loadBloqueadosCount]);

  // Iter 366 — Revisar casilla concilia@ desde la Bandeja (corre en backend en
  // segundo plano; polling al status hasta que termine y luego recarga).
  const [revisandoCasilla, setRevisandoCasilla] = useState(false);
  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const esperarFinRevision = useCallback((inicioIso) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const st = (await conciliacionProveedoresApi.emailIngestStatus()).data || {};
        const termino = !st.running && st.last_run && (!inicioIso || st.last_run >= inicioIso);
        if (!termino) return;
        clearInterval(pollRef.current); pollRef.current = null;
        setRevisandoCasilla(false);
        const r = st.last_result || {};
        const nErr = r.errores?.length ?? 0;
        const msg = `Casilla concilia@ revisada: ${r.procesados ?? 0} nuevo(s) · ${r.omitidos ?? 0} omitido(s) · ${nErr} error(es)`;
        if (nErr > 0) toast.warning(msg, { duration: 8000 }); else toast.success(msg, { duration: 6000 });
        load(); loadBloqueadosCount();
      } catch { /* seguir intentando */ }
    }, 3000);
  }, [load, loadBloqueadosCount]);

  const revisarCasilla = async () => {
    setRevisandoCasilla(true);
    try {
      const res = await conciliacionProveedoresApi.emailIngestRunNow();
      if (res.data?.already_running) {
        toast.info('Ya hay una revisión de la casilla en curso (cron o manual). Esperando que termine…');
        esperarFinRevision(null);
        return;
      }
      toast.info('Revisando casilla concilia@chirinosteel.com…');
      esperarFinRevision(res.data?.run_started_at || null);
    } catch (e) {
      setRevisandoCasilla(false);
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const handleSelect = async (item) => {
    try {
      const det = await conciliacionProveedoresApi.get(item.id);
      setSelected(det.data);
    } catch (e) {
      toast.error('No se pudo cargar el detalle');
    }
  };

  // Auto-seleccionar primer resultado cuando hay deep-link por RUT
  useEffect(() => {
    if (!rutFilter || selected || items.length === 0) return;
    const target = normRut(rutFilter);
    const match = items.find((it) => normRut(it.proveedor_rut) === target && it.estado !== 'superada');
    if (match) {
      conciliacionProveedoresApi.get(match.id).then((res) => setSelected(res.data)).catch(() => {});
    }
  // eslint-disable-next-line
  }, [rutFilter, items]);

  const filtered = useMemo(() => {
    let arr = items;
    if (rutFilter) {
      const target = normRut(rutFilter);
      arr = arr.filter((it) => normRut(it.proveedor_rut) === target);
    }
    // Iter 308.d — Búsqueda rápida: nombre proveedor, RUT o folio de factura del EDC
    const q = (searchQuery || '').trim().toLowerCase();
    if (q) {
      const qRut = normRut(q);
      arr = arr.filter((it) => {
        const nombre = (it.proveedor_nombre || '').toLowerCase();
        const rutN = normRut(it.proveedor_rut || '');
        if (nombre.includes(q)) return true;
        if (qRut && rutN && rutN.includes(qRut)) return true;
        // Match por folio dentro del matching
        const matching = it.matching || [];
        for (const m of matching) {
          const folioP = String(m.folio_proveedor || '').toLowerCase();
          const folioA = String(m.alegra_bill?.folio || '').toLowerCase();
          if (folioP.includes(q) || folioA.includes(q)) return true;
        }
        return false;
      });
    }
    if (filter === 'all') return arr.filter((it) => it.estado !== 'superada');
    return arr.filter((it) => it.estado === filter);
  }, [items, filter, rutFilter, searchQuery]);

  const counts = useMemo(() => ({
    all: items.filter((it) => it.estado !== 'superada').length,
    pendiente_revision: items.filter((it) => it.estado === 'pendiente_revision').length,
    resuelta: items.filter((it) => it.estado === 'resuelta').length,
    reenviado_corporativo: items.filter((it) => it.estado === 'reenviado_corporativo').length,
    aprobada: items.filter((it) => it.estado === 'aprobada').length,
    rechazada: items.filter((it) => it.estado === 'rechazada').length,
    superada: items.filter((it) => it.estado === 'superada').length,
  }), [items]);

  // ────────────────── Cmd+K: comandos contextuales ──────────────────
  const handleAprobarSeleccion = useCallback(async (accion) => {
    if (!selected) return;
    try {
      await conciliacionProveedoresApi.aprobar(selected.id, accion, '');
      toast.success(`Conciliación ${accion === 'aprobada' ? 'aprobada' : 'rechazada'}`);
      setSelected(null);
      await load();
      await loadBloqueadosCount();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }, [selected, load, loadBloqueadosCount]);

  const handleRematchSeleccion = useCallback(async () => {
    if (!selected) return;
    try {
      const res = await conciliacionProveedoresApi.rematch(selected.id);
      const t = res.data?.conciliacion?.totales || {};
      toast.success(
        `✓ Rematch: ${t.match_perfecto || 0} OK · ${t.match_parcial || 0} parcial · ${t.en_rcv_pendiente_contab || 0} en RCV · ${t.faltante || 0} faltante${res.data?.desbloqueo_automatico ? ' — Proveedor desbloqueado' : ''}`,
      );
      await load();
      await loadBloqueadosCount();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }, [selected, load, loadBloqueadosCount]);

  const isPendiente = selected?.estado === 'pendiente_revision';
  useRegisterCmdKCommands(
    'conciliacion-proveedores',
    'Conciliación Proveedores',
    [
      { id: 'cecp-subir', label: 'Subir estado de cuenta', sublabel: 'PDF/Excel del proveedor',
        icon: 'Plus', keywords: 'subir upload pdf excel estado cuenta',
        run: () => setUploadOpen(true) },
      { id: 'cecp-tab-bandeja', label: 'Ir a Bandeja', icon: 'ArrowRight',
        run: () => setTab('bandeja'), enabled: tab !== 'bandeja' },
      { id: 'cecp-tab-bloqueados', label: `Ir a Bloqueados${bloqueadosCount ? ` (${bloqueadosCount})` : ''}`,
        icon: 'ArrowRight', keywords: 'bloqueados bloqueo proveedor',
        run: () => setTab('bloqueados'), enabled: tab !== 'bloqueados' },
      { id: 'cecp-tab-config', label: 'Ir a Configuración', icon: 'Settings',
        run: () => setTab('config'), enabled: tab !== 'config' },
      { id: 'cecp-filter-pend', label: 'Filtrar: Pendientes revisión',
        sublabel: `${counts.pendiente_revision} conciliaciones`, icon: 'Filter',
        keywords: 'filtro pendientes revisar',
        run: () => { setTab('bandeja'); setFilter('pendiente_revision'); },
        enabled: filter !== 'pendiente_revision' || tab !== 'bandeja' },
      { id: 'cecp-filter-aprob', label: 'Filtrar: Aprobadas', icon: 'Filter',
        run: () => { setTab('bandeja'); setFilter('aprobada'); },
        enabled: filter !== 'aprobada' || tab !== 'bandeja' },
      { id: 'cecp-filter-rech', label: 'Filtrar: Rechazadas', icon: 'Filter',
        run: () => { setTab('bandeja'); setFilter('rechazada'); },
        enabled: filter !== 'rechazada' || tab !== 'bandeja' },
      { id: 'cecp-filter-all', label: 'Filtrar: Todas (sin superadas)', icon: 'RefreshCw',
        run: () => { setTab('bandeja'); setFilter('all'); },
        enabled: filter !== 'all' || tab !== 'bandeja' },
      { id: 'cecp-clear-rut', label: 'Limpiar filtro deep-link RUT', icon: 'X',
        sublabel: rutFilter ? `RUT: ${rutFilter}` : '',
        run: () => {
          window.history.replaceState({}, '', '/contabilidad/conciliacion-proveedores');
          window.location.reload();
        },
        enabled: !!rutFilter },
      { id: 'cecp-refresh', label: 'Refrescar bandeja', icon: 'RefreshCw',
        run: () => { load(); loadBloqueadosCount(); } },
      // Acciones sobre la conciliación seleccionada
      { id: 'cecp-aprobar-sel', label: 'Aprobar conciliación seleccionada',
        sublabel: selected ? `${selected.proveedor_nombre || 'proveedor'} · ${selected.periodo || ''}` : 'Seleccioná una primero',
        icon: 'CheckCircle', keywords: 'aprobar aceptar seleccionada',
        run: () => handleAprobarSeleccion('aprobada'),
        enabled: !!selected && isPendiente },
      { id: 'cecp-rechazar-sel', label: 'Rechazar conciliación seleccionada',
        sublabel: selected ? `${selected.proveedor_nombre || 'proveedor'}` : 'Seleccioná una primero',
        icon: 'XCircle', keywords: 'rechazar declinar seleccionada',
        run: () => handleAprobarSeleccion('rechazada'),
        enabled: !!selected && isPendiente },
      { id: 'cecp-rematch-sel', label: 'Rematch conciliación seleccionada',
        sublabel: 'Re-ejecuta matching contra Alegra (sin LLM)',
        icon: 'RefreshCw', keywords: 'rematch refrescar alegra matching',
        run: () => handleRematchSeleccion(),
        enabled: !!selected },
    ],
    [
      tab, filter, selected, bloqueadosCount, rutFilter, isPendiente,
      counts.pendiente_revision, load, loadBloqueadosCount,
      handleAprobarSeleccion, handleRematchSeleccion,
    ],
  );

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-[1600px] mx-auto" data-testid="conciliacion-proveedores-page">
      {rutFilter && (
        <div className="p-2.5 bg-blue-50 border border-blue-200 rounded flex items-center justify-between text-xs">
          <div>
            🔗 Deep-link activo: filtrando por RUT <strong className="font-mono">{rutFilter}</strong>
            {filtered.length === 0 && <span className="ml-2 text-amber-700">· sin conciliaciones para este RUT</span>}
          </div>
          <button
            onClick={() => { window.history.replaceState({}, '', '/contabilidad/conciliacion-proveedores'); window.location.reload(); }}
            className="text-blue-700 underline hover:text-blue-900"
            data-testid="btn-limpiar-filtro-rut"
          >
            Limpiar filtro
          </button>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <CircleDollarSign className="w-5 h-5 text-emerald-600" />
            Conciliación de Estados de Cuenta de Proveedores
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Cruza automáticamente los estados de cuenta enviados por proveedores contra Alegra. Bloquea pagos cuando faltan facturas anteriores a la nómina.
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)} data-testid="btn-subir-estado-cuenta">
          <Upload className="w-4 h-4 mr-1.5" /> Subir estado de cuenta
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList>
          <TabsTrigger value="bandeja" data-testid="tab-bandeja">Bandeja</TabsTrigger>
          <TabsTrigger value="bloqueados" data-testid="tab-bloqueados">
            Bloqueados{bloqueadosCount !== null ? ` (${bloqueadosCount})` : ''}
          </TabsTrigger>
          <TabsTrigger value="informe" data-testid="tab-informe">Informe Pendientes</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">Configuración</TabsTrigger>
        </TabsList>

        <TabsContent value="bandeja" className="mt-3">
          {/* Iter 308.d — Búsqueda rápida por proveedor / RUT / folio */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 max-w-md min-w-[220px]">
              <input
                type="text"
                placeholder="Buscar por proveedor, RUT o N° factura…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-9 px-3 pr-8 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="input-buscar-conciliacion"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                  data-testid="btn-limpiar-busqueda"
                >
                  ✕
                </button>
              )}
            </div>
            {searchQuery && (
              <span className="text-[11px] text-muted-foreground">
                {filtered.length} resultado(s)
              </span>
            )}
            {isGerencia && (
              <Button size="sm" variant="outline" onClick={revisarCasilla} disabled={revisandoCasilla}
                title="Lee ahora los correos nuevos de concilia@chirinosteel.com y crea las conciliaciones (el cron lo hace cada 30 min)"
                data-testid="btn-revisar-casilla-concilia">
                {revisandoCasilla
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Revisando casilla…</>
                  : <><MailSearch className="w-3.5 h-3.5 mr-1.5" /> Revisar casilla concilia@</>}
              </Button>
            )}
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            {['pendiente_revision', 'resuelta', 'reenviado_corporativo', 'aprobada', 'rechazada', 'superada', 'all'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filter === f ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-muted'}`}
                data-testid={`filter-${f}`}
              >
                {f === 'all' ? 'Todas'
                  : f === 'pendiente_revision' ? 'Pendientes'
                  : f === 'resuelta' ? '🔒 Resueltas'
                  : f === 'reenviado_corporativo' ? '📤 Reenviados'
                  : f === 'aprobada' ? 'Aprobadas'
                  : f === 'rechazada' ? 'Rechazadas'
                  : 'Superadas'}
                <span className="ml-1.5 text-[10px] opacity-80">{counts[f]}</span>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1">
              <BandejaList
                items={filtered}
                loading={loading}
                onSelect={handleSelect}
                selectedId={selected?.id}
                onRefresh={load}
              />
            </div>
            <div className="lg:col-span-2">
              <DetalleConciliacion
                item={selected}
                onAccion={() => { setSelected(null); load(); }}
                onRefresh={load}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="bloqueados" className="mt-3">
          <BloqueadosSection user={user} onChanged={() => { load(); loadBloqueadosCount(); }} />
        </TabsContent>

        <TabsContent value="informe" className="mt-3">
          <InformePendientesSection user={user} />
        </TabsContent>

        <TabsContent value="config" className="mt-3">
          <ConfiguracionSection user={user} />
        </TabsContent>
      </Tabs>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => load()}
      />
    </div>
  );
}
